import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { usePaymentViewModel } from '../viewmodels/PaymentViewModel';
import { FiChevronLeft, FiChevronRight, FiSearch, FiRefreshCw } from 'react-icons/fi';
import PaginationMobile from '../components/PaginationMobile';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { formatDateTimeKST } from '../utils/dateUtils';

// 모바일 페이지 컨테이너 - 밀도 있는 디자인
const MobilePageContainer = styled.div`
  min-height: 100vh;
  background-color: ${colors.semantic.background.secondary};
  padding: 12px 12px;
  padding-bottom: 100px; /* 하단 네비게이션 공간 확보 */

  @media (max-width: 768px) {
    padding: 8px 8px;
  }
`;

// 모바일 페이지 헤더 - 컴팩트
const MobilePageHeader = styled.div`
  margin-bottom: 12px;
`;

// 모바일 페이지 제목 - 더 작고 밀도 있게
const MobilePageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.secondary[800]};
  margin: 0 0 4px 0;
  line-height: 1.2;
`;

// 모바일 페이지 설명 - 컴팩트
const MobilePageDescription = styled.p`
  font-size: 12px;
  color: ${colors.semantic.text.tertiary};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

// 모바일 월 선택 컨테이너 - 컴팩트 디자인
const MobileMonthSelector = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.semantic.background.primary} 0%,
    ${colors.semantic.background.secondary} 100%
  );
  border-radius: ${borderRadius.lg};
  padding: 10px;
  box-shadow: ${shadows.sm};
  border: 1px solid ${colors.semantic.border.primary};
  margin-bottom: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, ${colors.primary[500]}, ${colors.primary[600]});
  }
`;

// 모바일 월 선택 헤더 - 컴팩트
const MobileMonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

// 모바일 월 선택 라벨 - 컴팩트 스타일
const MobileMonthLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  display: flex;
  align-items: center;
  gap: 4px;
  
  &::before {
    content: '📅';
    font-size: 14px;
  }
`;

// 모바일 월 표시 영역 - 컴팩트
const MobileCurrentMonth = styled.div`
  background: linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[700]});
  color: ${colors.semantic.text.inverse};
  padding: 4px 12px;
  border-radius: ${borderRadius['2xl']};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  min-width: 100px;
  box-shadow: ${shadows.primary};
`;

// 모바일 월 네비게이션 컨트롤 - 컴팩트
const MobileMonthControl = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

// 모바일 월 네비게이션 버튼 - 컴팩트 디자인
const MobileMonthNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, ${colors.secondary[100]}, ${colors.secondary[200]});
  border: 1px solid ${colors.secondary[300]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${shadows.sm};

  &:hover {
    background: linear-gradient(135deg, ${colors.secondary[200]}, ${colors.secondary[300]});
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${shadows.sm};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: ${shadows.sm};
  }

  svg {
    color: ${colors.semantic.text.secondary};
    transition: color 0.2s ease;
  }

  &:hover:not(:disabled) svg {
    color: ${colors.secondary[800]};
  }
`;


// 모바일 통계 카드 컨테이너 - 밀도 있게
const MobileStatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
`;

// 모바일 통계 카드 - 컴팩트
const MobileStatsCard = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  padding: 12px;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
  text-align: center;
`;

// 모바일 통계 값 - 작게
const MobileStatsValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.secondary[800]};
  margin-bottom: 2px;
  line-height: 1.2;
`;

// 모바일 통계 라벨 - 작게
const MobileStatsLabel = styled.div`
  font-size: 10px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
  line-height: 1.2;
`;

// 모바일 통계 변화량 - 작게
const MobileStatsChange = styled.div<{ isPositive?: boolean }>`
  font-size: 9px;
  color: ${props => (props.isPositive ? colors.success[600] : colors.error[600])};
  margin-top: 2px;
  line-height: 1.2;
`;

// 모바일 검색 바 컨테이너
const MobileSearchContainer = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: 16px;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
  margin-bottom: 16px;
`;

// 모바일 검색 입력 영역
const MobileSearchInputArea = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

// 모바일 검색 입력 컨테이너
const MobileSearchInputContainer = styled.div`
  position: relative;
  flex: 1;
`;

// 모바일 검색 입력 필드
const MobileSearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  font-size: 14px;
  background-color: ${colors.semantic.background.primary};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${colors.semantic.text.disabled};
  }
`;

// 모바일 검색 아이콘
const MobileSearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.semantic.text.tertiary};
  font-size: 16px;
`;

// 모바일 버튼 그룹
const MobileButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

// 모바일 검색 버튼
const MobileSearchButton = styled.button`
  padding: 10px;
  background-color: ${colors.primary[500]};
  color: ${colors.semantic.text.inverse};
  border: none;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  &:hover {
    background-color: ${colors.primary[600]};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// 모바일 초기화 버튼
const MobileResetButton = styled.button`
  padding: 10px;
  background-color: ${colors.neutral[100]};
  color: ${colors.semantic.text.secondary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  &:hover {
    background-color: ${colors.neutral[200]};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// 모바일 정렬 영역
const MobileSortArea = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 모바일 정렬 라벨
const MobileSortLabel = styled.span`
  font-size: 14px;
  color: ${colors.semantic.text.secondary};
  font-weight: 500;
`;

// 모바일 정렬 선택
const MobileSortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 14px;
  background-color: ${colors.semantic.background.primary};
  cursor: pointer;
  flex: 1;

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
  }
`;

// 모바일 결제 목록 컨테이너
const MobilePaymentList = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 결제 목록 헤더
const MobilePaymentListHeader = styled.div`
  background-color: ${colors.semantic.background.secondary};
  padding: 16px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 결제 목록 제목
const MobilePaymentListTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0;
`;

// 모바일 결제 카드
const MobilePaymentCard = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${colors.secondary[100]};
  
  &:last-child {
    border-bottom: none;
  }
`;

// 모바일 결제 카드 헤더
const MobilePaymentCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

// 모바일 결제 상품명
const MobilePaymentProduct = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.secondary[800]};
  margin: 0 0 4px 0;
  flex: 1;
`;

// 모바일 결제 금액
const MobilePaymentAmount = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.success[600]};
  text-align: right;
`;

// 모바일 결제 정보 그리드
const MobilePaymentInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 13px;
`;

// 모바일 결제 정보 아이템
const MobilePaymentInfoItem = styled.div`
  color: ${colors.semantic.text.tertiary};

  .label {
    font-weight: 500;
    margin-bottom: 2px;
  }

  .value {
    color: ${colors.semantic.text.secondary};
  }
`;

// 모바일 로딩 스피너
const MobileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid ${colors.semantic.border.primary};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 에러 메시지
const MobileErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: 16px;
  border-radius: ${borderRadius.lg};
  text-align: center;
  font-size: 14px;
  margin-bottom: 16px;
`;

// 모바일 빈 상태 메시지
const MobileEmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${colors.semantic.text.tertiary};

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
`;

// 모바일 결제 관리 페이지 컴포넌트
const PaymentsMobile: React.FC = () => {
  // ViewModel 사용
  const {
    // 상태
    payments,
    loading,
    error,
    currentPage,
    totalPages,
    total,
    limit,
    searchQuery: vmSearchQuery,
    yearMonth: vmYearMonth,
    sortBy,
    order,
    stats,
    statsLoading,
    statsError,

    // 액션
    fetchPaymentsAndStats,
    setSearchQuery: vmSetSearchQuery,
    setYearMonth: vmSetYearMonth,
    setSorting,
    setCurrentPage,
  } = usePaymentViewModel();

  // 로컬 상태 관리
  const [localSearchQuery, setLocalSearchQuery] = useState(vmSearchQuery);
  const [localYearMonth, setLocalYearMonth] = useState(vmYearMonth);

  const isInitialMount = useRef(true);

  // ViewModel 상태 동기화
  useEffect(() => {
    if (isInitialMount.current || vmSearchQuery !== localSearchQuery) {
      setLocalSearchQuery(vmSearchQuery);
    }
  }, [vmSearchQuery, localSearchQuery]);

  useEffect(() => {
    if (isInitialMount.current || vmYearMonth !== localYearMonth) {
      setLocalYearMonth(vmYearMonth);
    }
  }, [vmYearMonth, localYearMonth]);

  // 데이터 로드
  useEffect(() => {
    console.log('PaymentsMobile: 데이터 로드 시작');

    if (isInitialMount.current) {
      console.log('초기 마운트, API 호출');
      fetchPaymentsAndStats({ q: vmSearchQuery, year_month: vmYearMonth, page: 1, limit: limit, sort_by: sortBy, order: order });
      isInitialMount.current = false;
    } else {
      console.log('필터 변경 감지, API 호출');
      fetchPaymentsAndStats({ q: vmSearchQuery, year_month: vmYearMonth, page: 1 });
    }
  }, [vmYearMonth, vmSearchQuery, fetchPaymentsAndStats, limit, sortBy, order]);

  // 검색 실행 핸들러
  const handleSearchSubmit = useCallback(() => {
    console.log('🔍 PaymentsMobile - 검색 실행');
    vmSetSearchQuery(localSearchQuery);
    vmSetYearMonth(localYearMonth);
  }, [localSearchQuery, localYearMonth, vmSetSearchQuery, vmSetYearMonth]);

  // 월 변경 핸들러
  const handleMonthChange = (newYearMonth: string) => {
    console.log('📅 PaymentsMobile - 월 변경:', newYearMonth);
    setLocalYearMonth(newYearMonth);
    vmSetYearMonth(newYearMonth);
  };

  // 월 네비게이션 핸들러
  const handleMonthNavigate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(localYearMonth + '-01T00:00:00Z');
    if (direction === 'prev') {
      currentDate.setUTCMonth(currentDate.getUTCMonth() - 1);
    } else {
      currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
    }
    const newYearMonth = currentDate.toISOString().slice(0, 7);
    handleMonthChange(newYearMonth);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPaymentsAndStats({ page, q: localSearchQuery, year_month: localYearMonth, limit, sort_by: sortBy, order });
  };

  // 검색어 변경 핸들러
  const handleSearchQueryChange = (query: string) => {
    setLocalSearchQuery(query);
  };

  // 정렬 변경 핸들러
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [newSortBy, newOrder] = e.target.value.split('_');
    setSorting(newSortBy, newOrder as 'asc' | 'desc');
  };

  // 필터 초기화 핸들러
  const handleResetFilters = () => {
    console.log('🔄 PaymentsMobile - 필터 초기화');
    const defaultYearMonth = new Date().toISOString().slice(0, 7);
    const defaultSearchQuery = '';

    setLocalSearchQuery(defaultSearchQuery);
    setLocalYearMonth(defaultYearMonth);

    vmSetSearchQuery(defaultSearchQuery);
    vmSetYearMonth(defaultYearMonth);
  };

  // 금액 포맷팅
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ko-KR') + '원';
  };

  return (
    <MobilePageContainer>
      {/* 모바일 페이지 헤더 */}
      <MobilePageHeader>
        <MobilePageTitle>결제 관리</MobilePageTitle>
        <MobilePageDescription>결제 내역 조회 및 통계 관리</MobilePageDescription>
      </MobilePageHeader>

      {/* 월 선택 - 현대적 디자인 */}
      <MobileMonthSelector>
        <MobileMonthHeader>
          <MobileMonthLabel>조회 기준월</MobileMonthLabel>
          <MobileCurrentMonth>
            {localYearMonth ? `${localYearMonth.split('-')[0]}년 ${localYearMonth.split('-')[1]}월` : '월 선택'}
          </MobileCurrentMonth>
        </MobileMonthHeader>
        <MobileMonthControl>
          <MobileMonthNavButton
            onClick={() => handleMonthNavigate('prev')}
            disabled={loading}
            title="이전 달"
          >
            <FiChevronLeft size={16} />
          </MobileMonthNavButton>
          <MobileMonthNavButton
            onClick={() => handleMonthNavigate('next')}
            disabled={loading}
            title="다음 달"
          >
            <FiChevronRight size={16} />
          </MobileMonthNavButton>
        </MobileMonthControl>
      </MobileMonthSelector>

      {/* 에러 메시지 */}
      {error && (
        <MobileErrorMessage>
          {error}
        </MobileErrorMessage>
      )}

      {/* 통계 에러 메시지 */}
      {statsError && (
        <MobileErrorMessage>
          {statsError}
        </MobileErrorMessage>
      )}

      {/* 통계 카드 */}
      {statsLoading ? (
        <MobileLoadingContainer>
          <div className="spinner" />
        </MobileLoadingContainer>
      ) : stats ? (
        <MobileStatsContainer>
          <MobileStatsCard>
            <MobileStatsValue>{formatCurrency(stats.월결제금액)}</MobileStatsValue>
            <MobileStatsLabel>월 결제금액</MobileStatsLabel>
            {stats.증감금액 !== 0 && (
              <MobileStatsChange isPositive={stats.증감금액 > 0}>
                {stats.증감금액 > 0 ? '+' : ''}{formatCurrency(stats.증감금액)}
              </MobileStatsChange>
            )}
          </MobileStatsCard>
          <MobileStatsCard>
            <MobileStatsValue>{stats.월결제건수.toLocaleString()}건</MobileStatsValue>
            <MobileStatsLabel>월 결제 건</MobileStatsLabel>
            {stats.증감건수 !== 0 && (
              <MobileStatsChange isPositive={stats.증감건수 > 0}>
                {stats.증감건수 > 0 ? '+' : ''}{stats.증감건수}건
              </MobileStatsChange>
            )}
          </MobileStatsCard>
          <MobileStatsCard>
            <MobileStatsValue>{stats.월결제고객수.toLocaleString()}명</MobileStatsValue>
            <MobileStatsLabel>
              {localYearMonth ? `${Number(localYearMonth.split('-')[1])}월 결제 고객 수` : '결제 고객 수'}
            </MobileStatsLabel>
            {stats.증감고객수 !== 0 && (
              <MobileStatsChange isPositive={stats.증감고객수 > 0}>
                {stats.증감고객수 > 0 ? '+' : ''}{stats.증감고객수}명
              </MobileStatsChange>
            )}
          </MobileStatsCard>
        </MobileStatsContainer>
      ) : null}

      {/* 검색 바 */}
      <MobileSearchContainer>
        {/* 검색 입력 영역 */}
        <MobileSearchInputArea>
          <MobileSearchInputContainer>
            <MobileSearchIcon>
              <FiSearch />
            </MobileSearchIcon>
            <MobileSearchInput
              type="text"
              placeholder="상품명, 카드사, 유저명으로 검색..."
              value={localSearchQuery}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
            />
          </MobileSearchInputContainer>
        </MobileSearchInputArea>

        {/* 정렬 및 버튼 영역 */}
        <MobileSortArea>
          <MobileSortLabel>정렬:</MobileSortLabel>
          <MobileSortSelect
            value={`${sortBy}_${order}`}
            onChange={handleSortChange}
          >
            <option value="create_datetime_desc">승인일시 (최신순)</option>
            <option value="create_datetime_asc">승인일시 (오래된순)</option>
            <option value="결제금액_desc">결제금액 (높은순)</option>
            <option value="결제금액_asc">결제금액 (낮은순)</option>
            <option value="결제유저명_asc">유저명 (가나다순)</option>
            <option value="결제유저명_desc">유저명 (역순)</option>
          </MobileSortSelect>
          <MobileButtonGroup>
            <MobileSearchButton onClick={handleSearchSubmit}>
              <FiSearch size={16} />
            </MobileSearchButton>
            <MobileResetButton onClick={handleResetFilters}>
              <FiRefreshCw size={16} />
            </MobileResetButton>
          </MobileButtonGroup>
        </MobileSortArea>
      </MobileSearchContainer>

      {/* 결제 목록 */}
      <MobilePaymentList>
        <MobilePaymentListHeader>
          <MobilePaymentListTitle>
            결제 목록 {total > 0 && `(총 ${total.toLocaleString()}건)`}
          </MobilePaymentListTitle>
        </MobilePaymentListHeader>

        {/* 로딩 상태 */}
        {loading && (
          <MobileLoadingContainer>
            <div className="spinner" />
          </MobileLoadingContainer>
        )}

        {/* 빈 상태 */}
        {!loading && (!payments || payments.length === 0) && (
          <MobileEmptyState>
            <h3>결제 내역이 없습니다</h3>
            <p>검색 조건을 변경하거나 다른 월을 선택해보세요.</p>
          </MobileEmptyState>
        )}

        {/* 결제 목록 */}
        {!loading && payments && payments.length > 0 && (
          <>
            {payments.map((payment, index) => (
              <MobilePaymentCard key={index}>
                <MobilePaymentCardHeader>
                  <MobilePaymentProduct>{payment.결제상품명}</MobilePaymentProduct>
                  <MobilePaymentAmount>{formatCurrency(payment.결제금액)}</MobilePaymentAmount>
                </MobilePaymentCardHeader>

                <MobilePaymentInfoGrid>
                  <MobilePaymentInfoItem>
                    <div className="label">결제유저</div>
                    <div className="value">{payment.결제유저명}</div>
                  </MobilePaymentInfoItem>
                  <MobilePaymentInfoItem>
                    <div className="label">승인일시</div>
                    <div className="value">{formatDateTimeKST(payment.승인날짜)}</div>
                  </MobilePaymentInfoItem>
                  <MobilePaymentInfoItem>
                    <div className="label">결제수단</div>
                    <div className="value">{payment.결제수단}</div>
                  </MobilePaymentInfoItem>
                  <MobilePaymentInfoItem>
                    <div className="label">카드사</div>
                    <div className="value">{payment.카드사}</div>
                  </MobilePaymentInfoItem>
                </MobilePaymentInfoGrid>
              </MobilePaymentCard>
            ))}
          </>
        )}
      </MobilePaymentList>

      {/* 모바일 페이지네이션 */}
      {!loading && !error && total > 0 && (
        <PaginationMobile
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={handlePageChange}
        />
      )}
    </MobilePageContainer>
  );
};

export default PaymentsMobile; 